import { useState, useEffect } from "react";
import { api, type InquiryResult } from "../lib/api";

interface Props {
  open: boolean;
  initialName?: string;
  initialContact?: string;
  leadId?: string;
  onClose: () => void;
  onCreated: (result: InquiryResult) => void;
}

export function InquirySlideOver({ open, initialName = "", initialContact = "", leadId, onClose, onCreated }: Props) {
  const [name, setName] = useState(initialName);
  const [contact, setContact] = useState(initialContact);
  const [description, setDescription] = useState("");
  const [roughDate, setRoughDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InquiryResult | null>(null);

  useEffect(() => {
    if (open) {
      setName(initialName);
      setContact(initialContact);
      setDescription("");
      setRoughDate("");
      setError(null);
      setResult(null);
    }
  }, [open, initialName, initialContact]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await api.createInquiry({
        caller_name: name.trim(),
        contact: contact.trim(),
        description: description.trim() || undefined,
        rough_date: roughDate || undefined,
        lead_id: leadId || undefined,
      });
      setResult(r);
      onCreated(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <>
      <div
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 200 }}
        onClick={onClose}
      />
      <div
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(440px, 100vw)", background: "var(--surface)",
          zIndex: 201, padding: "1.5rem",
          boxShadow: "-4px 0 24px rgba(0,0,0,.15)",
          overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.25rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ margin: 0, fontSize: "1.15rem" }}>New inquiry</h2>
          <button onClick={onClose} className="btn-ghost" style={{ padding: "0.4rem 0.75rem" }}>&#x2715;</button>
        </div>

        {result ? (
          <div>
            <div className="alert" style={{ borderColor: "var(--accent)", background: "color-mix(in srgb, var(--accent) 10%, transparent)", marginBottom: "1rem" }}>
              <strong>{result.ClientName}</strong> added as a client and inquiry created.
            </div>
            <div style={{ display: "flex", gap: "0.75rem" }}>
              {result.Contact.includes("@") ? (
                <a href={`mailto:${result.Contact}`} className="btn-ghost" style={{ flex: 1, textAlign: "center" }}>&#x2709; Email</a>
              ) : (
                <a href={`tel:${result.Contact}`} className="btn-ghost" style={{ flex: 1, textAlign: "center" }}>&#x260E; Call</a>
              )}
              <a href="/app/bookings" onClick={onClose} className="btn-primary" style={{ flex: 1, textAlign: "center", textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
                View in pipeline &rarr;
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Caller name *</label>
                <input required value={name} onChange={e => setName(e.target.value)} placeholder="Emma de Vries" autoFocus />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Phone or email *</label>
                <input required value={contact} onChange={e => setContact(e.target.value)} placeholder="+31 6 12345678" />
              </div>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>What they want</label>
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Wedding, 2h outdoor, September" />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Rough date</label>
              <input type="date" value={roughDate} onChange={e => setRoughDate(e.target.value)} />
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? "Adding…" : "Add inquiry"}
            </button>
          </form>
        )}
      </div>
    </>
  );
}
